import { useState, useEffect } from "react";
import axios from "axios";
import { Gift, Copy, Share2, Users, CheckCircle, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function ReferralCard({ user }) {
  const [referralData, setReferralData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      fetchReferralData();
      fetchLeaderboard();
    }
  }, [user]);

  const fetchReferralData = async () => {
    try {
      const res = await axios.get(`${API}/referral/my-code`, { withCredentials: true });
      setReferralData(res.data);
    } catch (error) {
      console.error("Error fetching referral data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/referral/leaderboard`);
      setLeaderboard(res.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareReferral = () => {
    if (referralData?.referral_link) {
      const text = `Check your vehicle's Tax & MOT status free at IsItTaxed! Use my link and we both get 1 month Premium free: ${referralData.referral_link}`;
      
      if (navigator.share) {
        navigator.share({
          title: "IsItTaxed - Free Vehicle Checker",
          text: text,
          url: referralData.referral_link
        });
      } else {
        // Fallback to WhatsApp
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      }
    }
  };

  if (!user) {
    return (
      <Card className="border-2 border-dashed border-gray-300">
        <CardContent className="pt-6 text-center">
          <Gift className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <h3 className="font-bold text-gray-600 mb-2">Referral Program</h3>
          <p className="text-sm text-gray-500">
            Sign in to get your referral link and earn free Premium!
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <div className="animate-pulse">Loading referral data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <Gift className="h-5 w-5 text-yellow-600" />
          Refer Friends, Get Premium Free!
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Share your link - when a friend signs up, you <strong>both</strong> get 1 month Premium free!
        </p>

        {/* Referral Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-green-600">{referralData?.successful_referrals || 0}</p>
            <p className="text-xs text-gray-500">Successful</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-yellow-600">{referralData?.pending_referrals || 0}</p>
            <p className="text-xs text-gray-500">Pending</p>
          </div>
        </div>

        {/* Referral Code */}
        <div className="bg-white rounded-lg p-3 border-2 border-dashed border-yellow-300">
          <p className="text-xs text-gray-500 mb-1">Your Referral Code</p>
          <p className="font-mono text-lg font-bold text-gray-800">{referralData?.referral_code}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={copyReferralLink}
            variant="outline"
            className="flex-1"
          >
            {copied ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy Link
              </>
            )}
          </Button>
          <Button 
            onClick={shareReferral}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <Share2 className="h-4 w-4 mr-1" />
            Share
          </Button>
        </div>

        {/* Mini Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="mt-4 pt-4 border-t border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-4 w-4 text-yellow-600" />
              <p className="text-sm font-semibold text-gray-700">Top Referrers</p>
            </div>
            <div className="space-y-1">
              {leaderboard.slice(0, 3).map((entry, idx) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span className="text-gray-600">
                    {idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"} {entry.email}
                  </span>
                  <span className="font-semibold">{entry.referrals} referrals</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReferralCard;
